import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  Box,
  makeStyles
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)"
      : "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)",
    padding: theme.spacing(2),
    boxSizing: "border-box",
    zIndex: 9999,
    overflowY: "auto"
  },
  card: {
    padding: theme.spacing(4, 5),
    maxWidth: 460,
    width: "100%",
    textAlign: "center",
    borderRadius: 20,
    boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
    boxSizing: "border-box",
    margin: "auto"
  },
  iconSuccess: {
    fontSize: 76,
    color: "#28a745",
    marginBottom: theme.spacing(2)
  },
  iconError: {
    fontSize: 76,
    color: "#dc3545",
    marginBottom: theme.spacing(2)
  },
  iconInfo: {
    fontSize: 76,
    color: "#007bff",
    marginBottom: theme.spacing(2)
  },
  buttonPrimary: {
    marginTop: theme.spacing(3),
    padding: "12px 24px",
    fontWeight: "bold",
    borderRadius: 10,
    fontSize: "15px",
    textTransform: "none"
  }
}));

const VerifyEmail = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get("token");

      if (!token) {
        setLoading(false);
        setErrorMessage("No se proporcionó ningún token de activación en el enlace.");
        return;
      }

      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (data?.alreadyActive) {
          setAlreadyActive(true);
        } else {
          setSuccess(true);
        }
      } catch (err) {
        const backendError = err?.response?.data?.error;
        if (backendError && i18n.exists(`backendErrors.${backendError}`)) {
          setErrorMessage(i18n.t(`backendErrors.${backendError}`));
        } else if (backendError) {
          setErrorMessage(backendError);
        } else {
          setErrorMessage("El enlace de activación ha expirado (plazo de 7 días) o es inválido.");
        }
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [location.search]);

  return (
    <div className={classes.root}>
      <Paper elevation={6} className={classes.card}>
        {loading && (
          <Box py={4}>
            <CircularProgress size={56} style={{ color: "#0056b3" }} />
            <Typography variant="h6" style={{ marginTop: 24, fontWeight: 600 }}>
              Validando enlace de activación...
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Por favor espera un momento mientras activamos tu cuenta.
            </Typography>
          </Box>
        )}

        {!loading && success && (
          <Box py={1}>
            <CheckCircleOutlineIcon className={classes.iconSuccess} />
            <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 12 }}>
              ¡Cuenta Activada con Éxito! 🎉
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6 }}>
              Tu cuenta ha sido activada correctamente en el plan <strong>Freemium (De por vida)</strong>.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Ya puedes iniciar sesión con las credenciales que registraste.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              className={classes.buttonPrimary}
              onClick={() => history.push("/login")}
            >
              Iniciar Sesión
            </Button>
          </Box>
        )}

        {!loading && alreadyActive && (
          <Box py={1}>
            <InfoOutlinedIcon className={classes.iconInfo} />
            <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 12 }}>
              Cuenta Ya Activada
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6 }}>
              Esta cuenta ya se encontraba activada previamente. Puedes ingresar directamente al sistema.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              className={classes.buttonPrimary}
              onClick={() => history.push("/login")}
            >
              Ir al Inicio de Sesión
            </Button>
          </Box>
        )}

        {!loading && !success && !alreadyActive && (
          <Box py={1}>
            <ErrorOutlineIcon className={classes.iconError} />
            <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 12, color: "#dc3545" }}>
              Enlace Expirado o Inválido
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6 }}>
              {errorMessage || "El enlace de activación no es válido o ha superado el plazo máximo de 7 días."}
            </Typography>
            <Box mt={3} display="flex" flexDirection="column">
              <Button
                variant="contained"
                color="primary"
                fullWidth
                style={{ padding: "10px", borderRadius: 8, textTransform: "none", fontWeight: "bold" }}
                onClick={() => history.push("/login")}
              >
                Ir al Login
              </Button>
              <Button
                variant="outlined"
                fullWidth
                style={{ marginTop: 10, padding: "10px", borderRadius: 8, textTransform: "none" }}
                onClick={() => history.push("/signup")}
              >
                Volver a Registrarme
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </div>
  );
};

export default VerifyEmail;
